import React from 'react'
import { Separator } from '../../components/separator'
import { cn } from '../../lib/utils'

// Base Separator export - for other composed components
export { Separator as UISeparatorBase } from '../../components/separator'

export interface UISeparatorProps extends React.ComponentProps<typeof Separator> {
  variant?: 'default' | 'subtle' | 'bold'
  spacing?: 'tight' | 'normal' | 'loose'
}

/**
 * Enhanced Separator component following design system best practices
 * Provides consistent spacing and visual hierarchy
 */
export const UISeparator = React.forwardRef<
  React.ComponentRef<typeof Separator>,
  UISeparatorProps
>(({ className, variant = 'default', spacing = 'normal', orientation = 'horizontal', ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      orientation={orientation}
      className={cn(
        // Variant styles with improved visibility
        {
          'bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent': variant === 'default' && orientation === 'horizontal',
          'bg-gradient-to-b from-transparent via-gray-200 dark:via-gray-700 to-transparent': variant === 'default' && orientation === 'vertical',
          'bg-gradient-to-r from-transparent via-gray-100 dark:via-gray-800 to-transparent opacity-60': variant === 'subtle' && orientation === 'horizontal',
          'bg-gradient-to-b from-transparent via-gray-100 dark:via-gray-800 to-transparent opacity-60': variant === 'subtle' && orientation === 'vertical',
          'bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent h-0.5': variant === 'bold' && orientation === 'horizontal',
          'bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-600 to-transparent w-0.5': variant === 'bold' && orientation === 'vertical',
        },

        // Spacing styles
        {
          'my-2': spacing === 'tight' && orientation === 'horizontal',
          'my-4': spacing === 'normal' && orientation === 'horizontal',
          'my-6': spacing === 'loose' && orientation === 'horizontal',
          'mx-2': spacing === 'tight' && orientation === 'vertical',
          'mx-4': spacing === 'normal' && orientation === 'vertical',
          'mx-6': spacing === 'loose' && orientation === 'vertical',
        },

        // Shadow for depth (only on bold variant where visual weight is expected)
        variant === 'bold' && "shadow-sm shadow-black/[0.01] dark:shadow-black/[0.05]",

        className
      )}
      {...props}
    />
  )
})

UISeparator.displayName = "UISeparator"